import { FundCard } from "@/components/fund/FundCard";
import { BufferSeriesCard } from "@/components/fund/buffer-series-card";
import { FundFilters } from "@/components/fund/fund-filters";
import { LeveragedSingleStockSectors } from "@/components/fund/leveraged-single-stock-sectors";
import { EtfEyebrow } from "@/components/layout/EtfEyebrow";
import { EtfNewsletter } from "@/components/layout/EtfNewsletter";
import type { Fund } from "@/data/funds";
import type { FundType } from "@/data/types";
import { useFundList } from "@/hooks/use-fund-list";
import { formatAsOfMdy, fundLiveAumDollars } from "@/hooks/use-fund-live-data";
import { BRAND } from "@/lib/brand";
import { groupBufferBySeries } from "@/lib/fund-buffer-series";
import { CATEGORY_DESCRIPTIONS, type FundCategoryKey } from "@/lib/fund-categories";
import { leveragedSubcategory, thematicTickerSet } from "@/lib/fund-leveraged";
import { JsonLd, Seo, collectionPageLd } from "@/lib/seo";
/**
 * Products catalog. Ported from the dane etf-website `products/page.tsx`
 * (the pre-redesign design), driven from the static `@/data/funds` catalog
 * instead of the live `useEtfFundList` / `useFundsAumMap` hooks.
 *
 * Every fund-type section is ordered by live AUM descending (largest funds
 * first), mirroring HomePage.
 *
 * Buffer funds render as grouped `BufferSeriesCard`s — one card per series
 * (e.g. "buffer-useq") instead of one `FundCard` per monthly-roll fund — via
 * `groupBufferBySeries`. Thematic / 2x-leveraged / fixed keep the flat
 * `FundCard` grid.
 *
 * 2x Leveraged section is split into three sub-categories:
 *   1. Thematic    — affiliated 2x tracking a Corgi thematic ETF
 *   2. Core Equity — broad-market / country / commodity 2x
 *   3. Single Stocks — 2x daily on a single common stock (table view)
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";

const VISIBLE_ROWS = 2;
const COLS = 4;
const VISIBLE_LIMIT = VISIBLE_ROWS * COLS;

function formatAum(value: number): string {
  if (value <= 0) return "—";
  if (value >= 1_000_000_000) return `$${(value / 1_000_000_000).toFixed(2)}B`;
  if (value >= 950_000) return `$${(value / 1_000_000).toFixed(2)}M`;
  if (value >= 1_000) return `$${(value / 1_000).toFixed(0)}K`;
  return `$${value.toFixed(0)}`;
}

interface SubcategoryBannerProps {
  title: string;
  desc: string;
  count: number;
}

function SubcategoryBanner({ title, desc, count }: SubcategoryBannerProps) {
  return (
    <div className="relative flex items-baseline justify-between px-10 pt-[18px] pb-3 bg-[var(--etf-black)] before:content-[''] before:absolute before:left-[40px] before:top-0 before:w-[32px] before:h-[2px] before:bg-[var(--etf-orange)]">
      <div className="flex items-baseline gap-3">
        <span className="font-[family-name:var(--font-instrument)] text-[13px] font-semibold uppercase tracking-[0.16em] text-[var(--etf-white)]">
          {title}
        </span>
        <span className="font-[family-name:var(--font-instrument)] text-[12px] italic text-[var(--etf-white3)]">
          {desc}
        </span>
      </div>
      <span className="font-[family-name:var(--font-instrument)] text-[11px] uppercase tracking-[0.08em] text-[var(--etf-white3)]">
        {count} {count === 1 ? "fund" : "funds"}
      </span>
    </div>
  );
}

export function ProductsPage() {
  const [searchParams] = useSearchParams();
  const category = searchParams.get("category") as FundCategoryKey | null;

  const [activeCategory, setActiveCategory] = useState<FundCategoryKey>(category ?? "all");
  const [searchQuery, setSearchQuery] = useState("");
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({});

  useEffect(() => {
    if (category && category !== activeCategory) setActiveCategory(category);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [category]);

  const toggleSection = useCallback((type: string) => {
    setExpandedSections((prev) => ({ ...prev, [type]: !prev[type] }));
  }, []);

  // Live fund list from DDB (1 h TTL via Valkey). Falls back to [] while
  // loading — filters and counts show 0 until the query resolves.
  const { data: publicFunds = [] } = useFundList();

  // Build thematic-ticker set from the FULL unfiltered list so classification
  // is stable regardless of active filter/search.
  const thematicTickers = useMemo(() => thematicTickerSet(publicFunds), [publicFunds]);

  const filteredFunds = useMemo(() => {
    let result: readonly Fund[] = publicFunds;
    if (activeCategory !== "all") {
      // DDB uses `fundType` (not `type`).
      result = result.filter((f) => f.fundType === activeCategory);
    }
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter(
        (f) =>
          f.ticker.toLowerCase().includes(q) ||
          f.name.toLowerCase().includes(q) ||
          // DDB uses `theme` (optional) or falls back to `summary`.
          (f.theme ?? f.summary ?? "")
            .toLowerCase()
            .includes(q),
      );
    }
    return result;
  }, [publicFunds, activeCategory, searchQuery]);

  const groupedByType = useMemo(() => {
    const groups: Record<FundType, Fund[]> = {
      thematic: [],
      leveraged: [],
      buffer: [],
      fixed: [],
    };
    for (const fund of filteredFunds) {
      // DDB uses `fundType` (not `type`).
      groups[fund.fundType].push(fund);
    }
    // Order every section by live AUM descending so the largest funds surface
    // first (mirrors HomePage.tsx). AUM is the server-computed live value
    // (dollars); funds without a holdings snapshot sort to the bottom. Each
    // per-type array is freshly built here, so the in-place stable sort keeps
    // DDB list order as the tiebreaker. The leveraged sub-categories and
    // single-stock buckets inherit this order when they re-partition
    // `groups.leveraged` downstream.
    for (const type of Object.keys(groups) as FundType[]) {
      groups[type].sort((a, b) => (fundLiveAumDollars(b) ?? 0) - (fundLiveAumDollars(a) ?? 0));
    }
    return groups;
  }, [filteredFunds]);

  // Group the (AUM-sorted) buffer funds by series so we render one
  // BufferSeriesCard per series instead of one FundCard per monthly-roll fund.
  // Order follows groupedByType.buffer (AUM desc).
  const bufferSeriesGroups = useMemo(
    () => groupBufferBySeries(groupedByType.buffer),
    [groupedByType.buffer],
  );

  // Partition the (already-filtered) leveraged funds into 3 sub-categories.
  const leveragedGroups = useMemo(() => {
    const thematic: Fund[] = [];
    const coreEquity: Fund[] = [];
    const singleStocks: Fund[] = [];
    for (const fund of groupedByType.leveraged) {
      const sub = leveragedSubcategory(fund, thematicTickers);
      if (sub === "thematic") thematic.push(fund);
      else if (sub === "core-equity") coreEquity.push(fund);
      else if (sub === "single-stock") singleStocks.push(fund);
    }
    return { thematic, coreEquity, singleStocks };
  }, [groupedByType.leveraged, thematicTickers]);

  const hasAnyFunds = filteredFunds.length > 0;

  const tabCounts = useMemo(
    () =>
      ({
        all: publicFunds.length,
        thematic: publicFunds.filter((f) => f.fundType === "thematic").length,
        leveraged: publicFunds.filter((f) => f.fundType === "leveraged").length,
        buffer: publicFunds.filter((f) => f.fundType === "buffer").length,
        fixed: publicFunds.filter((f) => f.fundType === "fixed").length,
      }) satisfies Partial<Record<FundCategoryKey, number>>,
    [publicFunds],
  );

  // Total AUM sums the server-computed live AUM (SUM of holdings.market_value)
  // in dollars — no DDB.aum, no string parsing. "As of" = most-recent NAV-file
  // date (daily_nav rate_date, ISO sorts correctly) so it matches the fund
  // pages' NAV / price / AUM "as of" — NOT the holdings position_date.
  const totalAum = publicFunds.reduce((sum, f) => sum + (fundLiveAumDollars(f) ?? 0), 0);
  const latestLiveAsOf = publicFunds
    .map((f) => f.latestNavAsOfDate)
    .filter((d): d is string => Boolean(d))
    .sort()
    .pop();
  const latestAsOf = formatAsOfMdy(latestLiveAsOf);

  // Category-aware SEO: each filtered view self-canonicalizes to its
  // `?category=` URL; the unfiltered catalog canonicalizes to `/products`.
  const catMeta = activeCategory !== "all" ? CATEGORY_DESCRIPTIONS[activeCategory] : undefined;
  const seoTitle = catMeta?.title ?? "ETF Products";
  const seoDescription = catMeta
    ? `${catMeta.title} from Corgi Funds — ${catMeta.desc}. Compare holdings, expense ratios, and performance.`
    : "Browse the full Corgi Funds ETF lineup — thematic, 2x leveraged, structured buffer, and fixed-income ETFs. Compare holdings, expense ratios, and performance.";
  const seoPath = activeCategory !== "all" ? `/products?category=${activeCategory}` : "/products";

  return (
    <>
      <Seo title={seoTitle} description={seoDescription} canonicalPath={seoPath} />
      <JsonLd
        data={collectionPageLd({
          name: `${seoTitle} — Corgi Funds`,
          description: seoDescription,
          path: seoPath,
          items: filteredFunds.map((f) => ({
            name: `${f.ticker} — ${f.fullName ?? f.name}`,
            path: `/${f.id}`,
          })),
        })}
      />
      <section className="grid grid-cols-2 max-md:grid-cols-1 gap-20 max-md:gap-10 items-end px-10 max-md:px-6 pt-20 max-md:pt-12 pb-16 max-md:pb-10 border-b border-[var(--etf-border)]">
        <div>
          <EtfEyebrow>Our ETF Suite</EtfEyebrow>
          <h1 className="font-[family-name:var(--font-f37bolton)] text-[clamp(56px,7vw,96px)] font-normal leading-none tracking-[-0.02em] mb-0 [&>em]:not-italic [&>em]:text-[var(--etf-orange)]">
            Built for
            <br />
            the <em>Future.</em>
          </h1>
        </div>
        <div>
          <p className="text-base italic font-light text-[var(--etf-white2)] leading-[1.75] max-w-[460px] mb-8">
            Corgi's ETFs are designed to offer direct access to disruptive themes and companies
            shaping the future. From frontier technology to leveraged strategies&mdash; we build
            ETFs for what we believe comes next.
          </p>
          <div className="flex gap-10">
            <div>
              <div className="font-[family-name:var(--font-f37bolton)] text-4xl leading-none tracking-[-0.02em] text-[var(--etf-white)]">
                {totalAum > 0 ? formatAum(totalAum) : "—"}
              </div>
              <div className="font-[family-name:var(--font-instrument)] text-xs text-[var(--etf-white3)] tracking-[0.1em] uppercase mt-0.5">
                Total AUM{" "}
                <span className="text-[0.72em] opacity-60 font-normal block">
                  {latestAsOf ? `(as of ${latestAsOf})` : ""}
                </span>
              </div>
            </div>
            <div>
              <div className="font-[family-name:var(--font-f37bolton)] text-4xl leading-none tracking-[-0.02em] text-[var(--etf-white)]">
                {publicFunds.length}
              </div>
              <div className="font-[family-name:var(--font-instrument)] text-xs text-[var(--etf-white3)] tracking-[0.1em] uppercase mt-0.5">
                ETFs
              </div>
            </div>
            <div>
              <div className="font-[family-name:var(--font-f37bolton)] text-4xl leading-none tracking-[-0.02em] text-[var(--etf-white)]">
                {BRAND.foundedYear}
              </div>
              <div className="font-[family-name:var(--font-instrument)] text-xs text-[var(--etf-white3)] tracking-[0.1em] uppercase mt-0.5">
                Founded
              </div>
            </div>
          </div>
        </div>
      </section>

      <FundFilters
        activeCategory={activeCategory}
        onCategoryChange={setActiveCategory}
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
        funds={publicFunds}
        countsOverride={tabCounts}
      />

      <div id="fundsGrid">
        {!hasAnyFunds ? (
          <div className="flex flex-col items-center justify-center py-24 text-center">
            <div className="text-lg text-[var(--etf-white2)]">No funds found</div>
            <p className="mt-2 text-sm text-[var(--etf-white3)]">
              Try adjusting your search or filter criteria.
            </p>
          </div>
        ) : (
          // Section render order: Thematic → Buffer → 2x Leveraged → Fixed Income
          (["thematic", "buffer", "leveraged", "fixed"] as const).map((type) => {
            const funds = groupedByType[type];
            if (funds.length === 0) return null;
            const desc = CATEGORY_DESCRIPTIONS[type];

            // ── Leveraged section: split into 3 sub-categories ──────────────
            if (type === "leveraged") {
              const { thematic: thematicFunds, coreEquity, singleStocks } = leveragedGroups;
              return (
                <div key={type}>
                  {/* Main category banner */}
                  <div className="flex items-center justify-between px-10 py-4 bg-[var(--etf-black)] border-l-[3px] border-l-[var(--etf-orange)]">
                    <div className="flex items-baseline gap-3">
                      <span className="font-[family-name:var(--font-f37bolton)] text-lg font-medium tracking-[-0.01em] text-[var(--etf-white)]">
                        {desc?.title}
                      </span>
                      <span className="text-[13px] text-[var(--etf-white3)] italic font-light">
                        &middot; {desc?.desc}
                      </span>
                    </div>
                    <div className="flex items-center gap-4">
                      <span className="font-[family-name:var(--font-instrument)] text-xs text-[var(--etf-white3)] tracking-[0.08em]">
                        {funds.length} {funds.length === 1 ? "fund" : "funds"}
                      </span>
                      <button
                        type="button"
                        className="font-[family-name:var(--font-instrument)] text-xs tracking-[0.04em] text-[var(--etf-orange)] bg-transparent border-none cursor-pointer p-0 transition-opacity duration-[180ms] hover:opacity-80"
                        onClick={() => {
                          setActiveCategory(type);
                          window.scrollTo({ top: 0, behavior: "smooth" });
                        }}
                      >
                        View All &rarr;
                      </button>
                    </div>
                  </div>

                  {/* Sub-category 1: Thematic */}
                  {thematicFunds.length > 0 && (
                    <LeveragedSubcategoryCards
                      subcategoryKey="leveraged-thematic"
                      title="Thematic"
                      desc="· Affiliated 2x of Corgi thematic ETFs"
                      funds={thematicFunds}
                      expandedSections={expandedSections}
                      toggleSection={toggleSection}
                    />
                  )}

                  {/* Sub-category 2: Core Equity */}
                  {coreEquity.length > 0 && (
                    <LeveragedSubcategoryCards
                      subcategoryKey="leveraged-core-equity"
                      title="Core Equity"
                      desc="· Broad market, sector, country, commodity 2x"
                      funds={coreEquity}
                      expandedSections={expandedSections}
                      toggleSection={toggleSection}
                    />
                  )}

                  {/* Sub-category 3: Single Stocks (table view) */}
                  {singleStocks.length > 0 && (
                    <div>
                      <SubcategoryBanner
                        title="Single Stocks"
                        desc="· 2x daily exposure to a single common stock"
                        count={singleStocks.length}
                      />
                      <LeveragedSingleStockSectors funds={singleStocks} />
                    </div>
                  )}
                </div>
              );
            }

            // ── Thematic / Fixed: flat FundCard grid · Buffer: grouped series
            // cards (one BufferSeriesCard per series, in the same grid cell a
            // FundCard would occupy). See-more/collapse counts grid cells, so
            // buffer counts series cards rather than individual funds. ────────
            const isExpanded = expandedSections[type] ?? true;
            const isBuffer = type === "buffer";
            const cellCount = isBuffer ? bufferSeriesGroups.length : funds.length;
            const hiddenCount = cellCount - VISIBLE_LIMIT;
            const visibleFunds = isExpanded ? funds : funds.slice(0, VISIBLE_LIMIT);
            const visibleSeriesGroups = isExpanded
              ? bufferSeriesGroups
              : bufferSeriesGroups.slice(0, VISIBLE_LIMIT);
            return (
              <div key={type}>
                <div className="flex items-center justify-between px-10 py-4 bg-[var(--etf-black)] border-l-[3px] border-l-[var(--etf-orange)]">
                  <div className="flex items-baseline gap-3">
                    <span className="font-[family-name:var(--font-f37bolton)] text-lg font-medium tracking-[-0.01em] text-[var(--etf-white)]">
                      {desc?.title}
                    </span>
                    <span className="text-[13px] text-[var(--etf-white3)] italic font-light">
                      &middot; {desc?.desc}
                    </span>
                  </div>
                  <div className="flex items-center gap-4">
                    <span className="font-[family-name:var(--font-instrument)] text-xs text-[var(--etf-white3)] tracking-[0.08em]">
                      {isBuffer
                        ? `${bufferSeriesGroups.length} series · ${funds.length} funds`
                        : `${funds.length} ${funds.length === 1 ? "fund" : "funds"}`}
                    </span>
                    {type === "thematic" && (
                      <button
                        type="button"
                        className="font-[family-name:var(--font-instrument)] text-xs tracking-[0.04em] text-[var(--etf-orange)] bg-transparent border-none cursor-pointer p-0 transition-opacity duration-[180ms] hover:opacity-80"
                        onClick={() => {
                          setActiveCategory(type);
                          window.scrollTo({ top: 0, behavior: "smooth" });
                        }}
                      >
                        View All &rarr;
                      </button>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-4 max-lg:grid-cols-2 max-md:grid-cols-1 gap-px bg-[var(--etf-border)]">
                  {isBuffer
                    ? visibleSeriesGroups.map((g) => (
                        <BufferSeriesCard key={g.familyKey} seriesFunds={g.funds} />
                      ))
                    : visibleFunds.map((fund) => <FundCard key={fund.id} fund={fund} />)}
                </div>

                {hiddenCount > 0 && (
                  <button
                    type="button"
                    className="block w-full py-[18px] px-10 bg-[var(--etf-black)] border-none border-b border-b-[var(--etf-border)] text-[var(--etf-white2)] font-[family-name:var(--font-instrument)] text-sm font-medium tracking-[0.02em] cursor-pointer transition-[color,background] duration-200 text-center hover:text-[var(--etf-orange)] hover:bg-[var(--etf-black2)]"
                    onClick={() => toggleSection(type)}
                  >
                    {isExpanded
                      ? "Show less ▴"
                      : isBuffer
                        ? `See ${hiddenCount} more series ▾`
                        : `See ${hiddenCount} more fund${hiddenCount !== 1 ? "s" : ""} ▾`}
                  </button>
                )}
              </div>
            );
          })
        )}
      </div>

      <EtfNewsletter />
    </>
  );
}

// ─── Sub-component: card grid for a leveraged sub-category ───────────────────

interface LeveragedSubcategoryCardsProps {
  subcategoryKey: string;
  title: string;
  desc: string;
  funds: Fund[];
  expandedSections: Record<string, boolean>;
  toggleSection: (key: string) => void;
}

function LeveragedSubcategoryCards({
  subcategoryKey,
  title,
  desc,
  funds,
  expandedSections,
  toggleSection,
}: LeveragedSubcategoryCardsProps) {
  const isExpanded = expandedSections[subcategoryKey] ?? true;
  const visibleFunds = isExpanded ? funds : funds.slice(0, VISIBLE_LIMIT);
  const hiddenCount = funds.length - VISIBLE_LIMIT;

  return (
    <div>
      <SubcategoryBanner title={title} desc={desc} count={funds.length} />

      <div className="grid grid-cols-4 max-lg:grid-cols-2 max-md:grid-cols-1 gap-px bg-[var(--etf-border)]">
        {visibleFunds.map((fund) => (
          <FundCard key={fund.id} fund={fund} />
        ))}
      </div>

      {hiddenCount > 0 && (
        <button
          type="button"
          className="block w-full py-[18px] px-10 bg-[var(--etf-black)] border-none border-b border-b-[var(--etf-border)] text-[var(--etf-white2)] font-[family-name:var(--font-instrument)] text-sm font-medium tracking-[0.02em] cursor-pointer transition-[color,background] duration-200 text-center hover:text-[var(--etf-orange)] hover:bg-[var(--etf-black2)]"
          onClick={() => toggleSection(subcategoryKey)}
        >
          {isExpanded
            ? "Show less ▴"
            : `See ${hiddenCount} more fund${hiddenCount !== 1 ? "s" : ""} ▾`}
        </button>
      )}
    </div>
  );
}
